import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Box } from '@stvgroup/stv-ui-components'
import styles from './styles'
import parameterize from '../../utils/parameterize'
import getCrossDomainUrl from '../../utils/getCrossDomainUrl'
import AccountSummary from '../AccountSummary'
import { Chevron2Icon } from '../icons'
import LiveCardOverlay from '../LiveCardOverlay'
import ModalLayout from '../ModalLayout'
import { responsiveGlobalPadding } from '../../styles/layout'
import { colors } from '../../styles'

const PlayerMenu = ({
  id,
  items,
  hostname,
  activeItem,
  user,
  closeMenu,
  groupToken,
  renderCTVBanner,
  isSearchMenuActive,
  closeSearch,
  accountMenuActive,
  menuOpen,
  menuActive,
  isDesktop,
}) => {
  useEffect(() => {
    if (isSearchMenuActive) setShowLiveTV(false)
    if (accountMenuActive) setShowLiveTV(false)
  }, [isSearchMenuActive !== false, accountMenuActive])
  const isActiveItem = (item, url) => (url.split('/').filter(e => e !== '')[0] === item) || (item === '' && url === '/')
  const [showLiveTV, setShowLiveTV] = useState(false)
  const handleLiveTVClick = () => {
    // edge casey, but will update playerApiCache and if clicked at
    // the right time on live page could cause live player to think programme
    // hasn't updated and check every minute for eternity
    setShowLiveTV(!showLiveTV)
  }
  useEffect(() => {
    if (isSearchMenuActive) setShowLiveTV(false)
  }, [isSearchMenuActive])

  useEffect(() => {
    if (showLiveTV) closeSearch()
  }, [showLiveTV])
  const makeClassName = (stem, variant) => [stem, variant].reduce((acc, cur) => (
    `${acc} ${acc}--${parameterize(cur)}`
  ))
  const accountSummary = user && user.firstName && activeItem === 'account' ? (
    <AccountSummary
      userInitial={user.firstName.charAt(0)}
      userName={`${user.firstName} ${user.lastName}`}
      currentlySubscribed={user.currentlySubscribed}
      vipOptIn={user.vipOptIn}
      border={false}
    />
  ) : null

  const containerSx = useMemo(() => (
    isDesktop
      ? {
        pt: { xl: 0 },
        pb: { xl: 0 },
        display: { xs: 'none', xl: 'flex' },
        alignItems: { xl: 'center' },
      }
      : {
        pt: { xl: 0 },
        pb: { xl: 0 },
        display: menuOpen ? { xs: 'block', xl: 'none' } : { xs: 'none', xl: 'none' },
      }
  ), [isDesktop, menuOpen])

  const contentSx = useMemo(() => ({
    px: {
      xs: responsiveGlobalPadding.xs,
      sm: responsiveGlobalPadding.sm,
      md: 0,
    },
    display: isDesktop ? 'flex' : 'block',
    justifyContent: isDesktop ? 'flex-end' : 'center',
  }), [isDesktop])

  const menuBodySx = useMemo(() => ({
    display: 'flex',
    alignItems: isDesktop ? 'center' : 'flex-start',
    justifyContent: isDesktop ? 'flex-end' : 'center',
    width: '100%',
    pt: isDesktop ? 0 : 6,
  }), [isDesktop])

  const listSx = useMemo(() => (
    isDesktop
      ? {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: '16px',
        padding: 0,
        margin: 0,
        listStyle: 'none',
      }
      : {
        display: 'block',
        padding: 0,
        margin: 0,
        listStyle: 'none',
        textAlign: 'center',
        fontSize: '24px',
        fontWeight: 600,
        lineHeight: '30px',
      }
  ), [isDesktop])

  const menuItemBaseSx = useMemo(() => (
    isDesktop
      ? {
        display: 'flex',
        alignItems: 'center',
        height: '35px',
        fontWeight: 600,
      }
      : {
        width: '270px',
        borderRadius: '8px',
        mb: '10px',
        mx: 'auto',
        display: 'flex',
        justifyContent: 'center',
      }
  ), [isDesktop])

  const menuLinkSx = useMemo(() => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    minHeight: '35px',
    width: '100%',
    px: '12px',
    borderRadius: '8px',
    color: colors.primaryText,
    fontSize: '16px',
    fontWeight: 600,
    lineHeight: '20px',
    transition: 'background 0.2s ease, color 0.2s ease',
    textAlign: isDesktop ? 'left' : 'center',
    '&:hover, &:focus': {
      backgroundColor: colors.ctvBg,
    },
  }), [isDesktop])

  return (
    <ModalLayout
      className={`PlayerMenu ${isDesktop ? 'desktop' : 'mobile'} menu-hidden-until-open ${menuActive ? 'active' : ''} ${user?.signedIn ? 'logged-in' : 'logged-out'}`}
      data-testid="player-menu"
      sx={containerSx}
    >
      <style jsx>{styles}</style>
      {!isDesktop && (
        <ModalLayout.Header
          onClose={closeMenu}
          startSlot={accountSummary}
          closeButtonProps={{ ariaLabel: 'Close menu' }}
          sx={{
            mb: 2,
            display: 'flex',
            pr: '9px',
            alignItems: 'center',
          }}
        />
      )}
      <ModalLayout.Content sx={contentSx}>
        <Box className="menu-body" sx={menuBodySx}>
          <Box
            component="ul"
            id={id}
            className="menu-list"
            sx={listSx}
          >
            {items.map((menuItem) => {
              const isUpsellItem = ['subscribe to stv player+', 'upgrade to stv player+']
                .includes(menuItem.title?.toLowerCase?.())
              const menuItemSx = isUpsellItem && isDesktop
                ? { display: 'none' }
                : {
                  ...menuItemBaseSx,
                  '&.active a': {
                    backgroundColor: colors.primaryText,
                    color: colors.textInput,
                  },
                  '&.active.menu-item--premier-sports a': {
                    backgroundColor: 'yellow',
                    color: 'black',
                  },
                  '&:hover:not(.active) a, &:focus-within:not(.active) a': {
                    backgroundColor: colors.ctvBg,
                  },
                  '&.menu-item--premier-sports:hover a, &.menu-item--premier-sports:focus-within a': {
                    backgroundColor: 'yellow',
                    color: 'black',
                  },
                }

              return (
                <Box
                  component="li"
                  key={parameterize(`player-menu-${menuItem.title}`)}
                  className={`menu-item ${isActiveItem(activeItem, menuItem.url) ? ' active' : ''} ${makeClassName('menu-item', menuItem.title)}`}
                  sx={menuItemSx}
                >
                  {menuItem.title === 'Live TV' && isDesktop ? (
                    // eslint-disable-next-line jsx-a11y/anchor-is-valid
                    <Box
                      component="a"
                      onClick={handleLiveTVClick}
                      onKeyDown={handleLiveTVClick}
                      role="presentation"
                      className={`live-card ${showLiveTV ? 'active' : ''} ${makeClassName('menu-link', menuItem.title)}`}
                      sx={{
                        ...menuLinkSx,
                        cursor: 'pointer',
                        gap: '8px',
                        justifyContent: 'flex-start',
                      }}
                    >
                      {menuItem.title}
                      <Chevron2Icon direction="down" />
                    </Box>
                  ) : (
                    // Render other menu items
                    <Box
                      component="a"
                      href={getCrossDomainUrl({
                        path: menuItem.url,
                        subdomain: 'player',
                        hostname,
                      })}
                      className={`${makeClassName('menu-link', menuItem.title)}`}
                      sx={menuLinkSx}
                    >
                      {menuItem.title}
                    </Box>
                  )}
                </Box>
              )
            })}
          </Box>
        </Box>
        {showLiveTV && (
          <LiveCardOverlay
            groupToken={groupToken}
            renderCTVBanner={renderCTVBanner}
          />
        )}
      </ModalLayout.Content>
    </ModalLayout>
  )
}
PlayerMenu.propTypes = {
  id: PropTypes.string,
  hostname: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      url: PropTypes.string,
    })
  ),
  activeItem: PropTypes.string,
  user: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    signedIn: PropTypes.bool,
    vipOptIn: PropTypes.bool,
    currentlySubscribed: PropTypes.bool,
  }),
  closeMenu: PropTypes.func,
  groupToken: PropTypes.string.isRequired,
  renderCTVBanner: PropTypes.bool,
  isSearchMenuActive: PropTypes.bool,
  closeSearch: PropTypes.func,
  accountMenuActive: PropTypes.bool,
  menuOpen: PropTypes.bool,
  menuActive: PropTypes.bool,
  isDesktop: PropTypes.bool,
}
PlayerMenu.defaultProps = {
  id: '',
  items: [],
  activeItem: '',
  user: null,
  closeMenu: () => { },
  renderCTVBanner: false,
  isSearchMenuActive: false,
  closeSearch: () => { },
  accountMenuActive: false,
  menuOpen: false,
  menuActive: false,
  isDesktop: true,
}
export default PlayerMenu
